import { useEffect, useState, useRef } from 'react';
import { isNullOrUndefined } from 'utils/Global';
import withPerf from 'components/customComponents/withPerf';
import { AnnotationLoaderProps } from 'business/models/CustomModels';
import { useRMDispatch, useRMSelector } from 'redux/hooks';
import {
  selectCurrentWellScores,
  selectHideAutoScores,
  selectScoreGroups,
  selectScoringWell,
} from 'redux/selectors/StateSelectors';
import {
  selectExp,
  selectExperimentPlates,
} from 'redux/selectors/ExperimentStateSelector';
import { cloneDeep } from 'lodash';
import { setCurrentWellScores } from 'redux/reducers/experimentDataReducer';
import { ScoresOfWellDrops } from 'components/Experiment/constants';
import {
  useSelectedDropImageParameters,
  useLastSelectedWellNavigationDetails,
} from 'redux/hooks/experiment';
import { filterScores } from 'components/searchAndFilter/utils';

function AnnotationLoader(props: AnnotationLoaderProps) {
  const dispatch = useRMDispatch();
  const { response } = props;
  const plates = useRMSelector(selectExperimentPlates);
  const plate = plates.find((p) => p.plateNumber === props.plateNumber);
  const [scores, setScores] = useState([]);
  const [scribbles, setScribbles] = useState([]);
  const [roi, setRoi] = useState([]);
  const [annotations, setAnnotations] = useState([]);
  const [disabledImages, setDisabledImages] = useState([]);
  const [histogramImages, setHistogramImages] = useState([]);
  const [wellNumsContainingScribbles, setwellNumsContainingScribbles] = useState([]);

  const refresh = useRef(false); // multiple useEffects are batched togehter in one render cycle, and useState will get old value
  const scoreGroups = useRMSelector(selectScoreGroups);
  const exp = useRMSelector(selectExp);
  const currentWellScores = useRMSelector(selectCurrentWellScores);
  const scoringWell = useRMSelector(selectScoringWell);
  const hideAutoScores = useRMSelector(selectHideAutoScores);
  const wellNavigationDetails = useLastSelectedWellNavigationDetails();
  const selectedDropImageParameters = useSelectedDropImageParameters();
  const plateID = plate?.plateId;
  const { wellNum } = wellNavigationDetails || {};
  const { dropNumber, inspection, regionName, cpId } = selectedDropImageParameters || {};
  const imageBatchId = inspection?.groupedInspections?.find(
    (gi) => gi.plateId === plateID,
  )?.imageBatchId;

  const [wellDropScores, setWellDropScores] = useState<any>();

  useEffect(() => {
    if (plate) {
      const plateScores = getActiveCanvasScores();
      setWellDropScores(plateScores);
    }
  }, [plate]);

  function getActiveCanvasScores() {
    let tempScores;
    if (plate.plateScores != null || plate.plateScores != undefined) {
      if (hideAutoScores) {
        tempScores = plate.plateScores.map((score) =>
          score.filter(
            (ibscores) =>
              ibscores.scoreBatchId === imageBatchId &&
              ibscores.dropNumber === dropNumber &&
              ibscores.isAutoScore === false,
          ),
        );
      } else {
        tempScores = plate.plateScores.map((score) =>
          score.filter(
            (ibscores) =>
              ibscores.scoreBatchId === imageBatchId &&
              ibscores.dropNumber === dropNumber,
          ),
        );
      }
    }
    return tempScores;
  }

  function getActiveCanvasScribbles() {
    if (!isNullOrUndefined(response.plateScribblesDetail)) {
      if (!isNullOrUndefined(response.plateScribblesDetail.plateScribbles)) {
        setScribbles([]);
        const s = response.plateScribblesDetail.plateScribbles.filter(
          (scribble) => scribble.scribbleStartPointsXYofWell.dropNumber === dropNumber,
        );
        setScribbles(s);
      }
    }
  }

  useEffect(() => {
    let tempScores;
    if (!isNullOrUndefined(plate) && !isNullOrUndefined(plate.plateScores)) {
      if (hideAutoScores) {
        tempScores = wellDropScores?.map((score) =>
          score.filter((fscores) => fscores.isAutoScore == false),
        );
      } else {
        tempScores = plate.plateScores.map((score) =>
          score.filter(
            (fscores) =>
              fscores.scoreBatchId == imageBatchId && fscores.dropNumber == dropNumber,
          ),
        );
      }
      setWellDropScores(tempScores);
    }
  }, [hideAutoScores]);

  useEffect(() => {
    if (isNullOrUndefined(wellDropScores)) {
      return;
    }
    const tempScores = wellDropScores.map((score) =>
      filterScores(scoreGroups, score, hideAutoScores),
    );
    /**
     * RFR: We need to use only redux state for scores
     */
    setScores(tempScores);
  }, [wellDropScores, scoreGroups, hideAutoScores]);

  useEffect(() => {
    const { layerRef } = props.canvasRefs();
    if (layerRef != undefined) {
      layerRef.batchDraw();
    }
  }, [refresh.current]);

  useEffect(() => {
    const drawScoresPlateDrawing = () => {
      try {
        if (
          !plate.drawingIdentifiers ||
          !wellNavigationDetails ||
          !selectedDropImageParameters
        ) {
          return null;
        }

        const s = plate.drawingIdentifiers.plateScribblesDetail.plateScribbles.filter(
          (scribble) => scribble.scribbleStartPointsXYofWell.dropNumber === dropNumber,
        );
        setScribbles(s);
        setwellNumsContainingScribbles(
          plate.drawingIdentifiers.plateScribblesDetail.wellNumsContainingScribbles,
        );
        setRoi(plate.drawingIdentifiers.plateROI);
        setAnnotations(plate.drawingIdentifiers.plateAnnotations);
        setHistogramImages(
          plate.drawingIdentifiers.plateHistograms
            .filter(
              (x) =>
                x.regionName === regionName &&
                x.dropNumber === dropNumber &&
                x.cpId === cpId &&
                x.imageBatchId === imageBatchId,
            )
            .map((x) => x.wellNumber),
        );
        const disabledWellImages = plate.drawingIdentifiers.plateDropRegions
          .filter(
            (x) =>
              x.regionName === regionName &&
              x.plateId === plate.plateId &&
              x.dropNumber === dropNumber &&
              x.imagingEnabled === false,
          )
          ?.map((img) => img.wellNumber);
        setDisabledImages(disabledWellImages);

        if (plate.plateId === plateID) {
          if (plate?.plateScores?.length) {
            const wellOneDetails: ScoresOfWellDrops[] = plate.plateScores.find((score) =>
              score.some((score) => score.wellNumber === wellNum),
            );
            dispatch(setCurrentWellScores(wellOneDetails));
          }
        }
        const tempScores = getActiveCanvasScores();
        setWellDropScores(tempScores);
      } catch (err) {
        console.error(err);
      }
      const tempScores = getActiveCanvasScores();
      setWellDropScores(tempScores);
    };

    drawScoresPlateDrawing();
  }, [plate?.drawingIdentifiers, wellNavigationDetails, selectedDropImageParameters]);

  useEffect(() => {
    const drawingResponse = cloneDeep(response);
    function applyScoring(scoreWell) {
      let wellIndex = -1;
      wellIndex = drawingResponse.plateScores.findIndex((score) =>
        score.some((wds) => wds.wellNumber === wellNum),
      );
      if (wellIndex === -1) {
        drawingResponse.plateScores.push(scoreWell);
      } else {
        drawingResponse.plateScores.splice(wellIndex, 1, scoreWell);
      }
      const tempScores = getActiveCanvasScores();
      props.setResponse(drawingResponse);
      setWellDropScores(tempScores);
    }

    if (
      scoringWell != undefined &&
      currentWellScores != undefined &&
      plate.plateId === plateID &&
      !isNullOrUndefined(response.plateScores) &&
      exp.isImageModeActive
    ) {
      applyScoring(currentWellScores);
    }
  }, [scoringWell]);

  useEffect(() => {
    if (plate.plateId === plateID) {
      const tempScores = getActiveCanvasScores();
      setWellDropScores(tempScores);
      getActiveCanvasScribbles();
    }
  }, [imageBatchId, dropNumber]);

  useEffect(() => {
    if (plate.plateId === plateID && !isNullOrUndefined(plate.plateScores)) {
      const currWellScores: ScoresOfWellDrops[] = plate.plateScores.find((score) =>
        score.some((score) => score.wellNumber === wellNum),
      );
      if (!isNullOrUndefined(currWellScores)) {
        dispatch(setCurrentWellScores(currWellScores));
      } else {
        dispatch(setCurrentWellScores([]));
      }
    }
  }, [wellNum]);

  if (refresh.current) {
    refresh.current = false;
  }

  if (!props.render) {
    return null;
  }

  return props.render({
    plate,
    scores,
    wellNumsContainingScribbles,
    scribbles,
    annotations,
    roi,
    disabledImages,
    histogramImages,
    dropNumber,
  });
}

export default withPerf(AnnotationLoader);
